import * as React from 'react';

type Theme = 'light' | 'dark' | 'system' | 'auto';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
  attribute?: 'class' | `data-${string}`;
  enableSystem?: boolean;
  disableTransitionOnChange?: boolean;
};

type ThemeContextValue = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'light' | 'dark';
  systemTheme: 'light' | 'dark';
};

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredTheme(storageKey: string): Theme | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = localStorage.getItem(storageKey);
    if (value === 'light' || value === 'dark' || value === 'system' || value === 'auto') {
      return value;
    }
  } catch (_) {
    // noop
  }
  return null;
}

function setDomTheme(attribute: ThemeProviderProps['attribute'], resolvedTheme: 'light' | 'dark') {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  if (attribute === 'class') {
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
    return;
  }

  root.setAttribute(attribute || 'data-theme', resolvedTheme);
}

function withOptionalTransitionSuppressed(enabled: boolean, fn: () => void) {
  if (typeof document === 'undefined' || !enabled) {
    fn();
    return;
  }

  const style = document.createElement('style');
  style.appendChild(
    document.createTextNode('*{transition:none!important;-webkit-transition:none!important}')
  );
  document.head.appendChild(style);

  fn();

  // Force style flush before removing.
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  window.getComputedStyle(document.body).opacity;
  requestAnimationFrame(() => {
    style.remove();
  });
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'theme',
  attribute = 'class',
  enableSystem = true,
  disableTransitionOnChange = false,
}: ThemeProviderProps) {
  const systemTheme = React.useMemo(() => getSystemTheme(), []);

  const [theme, setThemeState] = React.useState<Theme>(() => {
    const stored = readStoredTheme(storageKey);
    return stored ?? defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = React.useState<'light' | 'dark'>(() => {
    const initial = readStoredTheme(storageKey) ?? defaultTheme;
    if (initial === 'light' || initial === 'dark') return initial;
    return getSystemTheme();
  });

  const applyTheme = React.useCallback(
    (nextTheme: Theme) => {
      const shouldUseSystem = enableSystem && (nextTheme === 'system' || nextTheme === 'auto');
      const nextResolved: 'light' | 'dark' = shouldUseSystem ? getSystemTheme() : (nextTheme as 'light' | 'dark');

      withOptionalTransitionSuppressed(disableTransitionOnChange, () => {
        setDomTheme(attribute, nextResolved);
      });
      setResolvedTheme(nextResolved);
    },
    [attribute, disableTransitionOnChange, enableSystem]
  );

  React.useEffect(() => {
    applyTheme(theme);
  }, [applyTheme, theme]);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !enableSystem) return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (theme === 'system' || theme === 'auto') {
        applyTheme(theme);
      }
    };

    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [applyTheme, enableSystem, theme]);

  const setTheme = React.useCallback(
    (nextTheme: Theme) => {
      setThemeState(nextTheme);
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(storageKey, nextTheme);
        } catch (_) {
          // noop
        }
      }
    },
    [storageKey]
  );

  const value = React.useMemo<ThemeContextValue>(() => {
    return {
      theme,
      setTheme,
      resolvedTheme,
      systemTheme,
    };
  }, [resolvedTheme, setTheme, systemTheme, theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

export type { ThemeProviderProps };
